import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import ImageUploader from '../../components/ImageUploader';
import MainEventerTimingFields from '../../components/roster/MainEventerTimingFields';
import FormInfoTip from '../../components/ui/FormInfoTip';
import {
    buildChangeDescription,
    formatAdminBoolean,
    formatAdminDate,
    recordAdminUpdate
} from '../../utils/adminUpdates';
import { rosterFormInfo } from '../../utils/rosterFormInfo';
import {
    MAIN_EVENTER_COOLDOWN_DAYS,
    MAIN_EVENTER_DURATION_DAYS,
    clampMainEventerTimingDays
} from '../../utils/mainEventerCooldown';

function normalizeRosterDate(value) {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value.toDate === 'function') return value.toDate();
    if (typeof value === 'number') {
        const parsedFromNumber = new Date(value);
        return Number.isNaN(parsedFromNumber.getTime()) ? null : parsedFromNumber;
    }
    if (typeof value === 'string') {
        const parsedFromString = Date.parse(value);
        return Number.isNaN(parsedFromString) ? null : new Date(parsedFromString);
    }
    return null;
}

function getRosterOwnerLabel(roster, usersMap) {
    if (roster?.ownerUid && usersMap.has(roster.ownerUid)) {
        const owner = usersMap.get(roster.ownerUid);
        return owner?.displayName || owner?.username || owner?.email || roster.ownerUid;
    }
    return roster?.owner || roster?.ownerUid || 'FantaWrestling';
}

function getRosterFilterLabel(roster) {
    if (roster?.onlyMainRoster && roster?.onlyNXT) return 'Main + NXT';
    if (roster?.onlyMainRoster) return 'Main';
    if (roster?.onlyNXT) return 'NXT';
    return 'Nessuno';
}

function isRosterDraft(roster) {
    return roster?.gameType === 'Indipendente' && roster?.isPublished === false;
}

function getRosterStatus(roster) {
    if (isRosterDraft(roster)) {
        return {
            label: 'Bozza',
            className: 'bg-warning text-dark',
            icon: 'bi-clock-history'
        };
    }

    if (roster?.gameType === 'Indipendente') {
        return {
            label: 'Iscrizioni aperte',
            className: 'bg-success',
            icon: 'bi-broadcast'
        };
    }

    return {
        label: 'Attivo',
        className: 'bg-brand-secondary text-brand-primary',
        icon: 'bi-trophy'
    };
}

function getRosterValidationError(form) {
    if (!form.name?.trim()) {
        return 'Il nome del roster è obbligatorio';
    }
    if (!form.dataInizioPunteggio) {
        return 'La data inizio punteggio è obbligatoria';
    }
    if (form.onlyMainRoster && form.onlyNXT) {
        return 'Non puoi attivare contemporaneamente Solo Main Roster e Solo NXT';
    }

    const maxBackstageCount = Math.max((Number(form.maxWrestlers) || 0) - (form.hasMainEventer ? 1 : 0), 0);
    if (form.hasBackstage && (form.backstageCount < 1 || form.backstageCount > maxBackstageCount)) {
        return `Il numero di wrestler in backstage deve essere compreso tra 1 e ${maxBackstageCount}`;
    }

    return '';
}

export default function AddRoster() {
    const [users, setUsers] = useState([]);
    const [rosters, setRosters] = useState([]);
    const usersMap = useMemo(
        () => new Map(users.filter(Boolean).map(user => [user.id, user])),
        [users]
    );
    const [editingId, setEditingId] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [form, setForm] = useState({
        name: '',
        dataInizioPunteggio: '',
        maxWrestlers: 8,
        maxSwap: 8,
        maxCredits: 400,
        owner: 'FantaWrestling',
        gameType: 'Indipendente',
        imageUrl: '',
        description: '',
        minMaleWrestlers: 0,
        minFemaleWrestlers: 0,
        onlyMainRoster: false,
        onlyNXT: false,
        hasBackstage: false,
        backstageCount: 0,
        hasMainEventer: true,
        mainEventerDurationDays: MAIN_EVENTER_DURATION_DAYS,
        mainEventerCooldownDays: MAIN_EVENTER_COOLDOWN_DAYS,
        isGM: false,
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const currentEditingRoster = useMemo(
        () => rosters.find(roster => roster.id === editingId) || null,
        [editingId, rosters]
    );
    const isEditingDraft = isRosterDraft(currentEditingRoster);
    const isCompetitiveEditLocked = Boolean(editingId && form.gameType === 'Competitivo');
    const canSaveDraft = form.gameType === 'Indipendente' && (!editingId || isEditingDraft);

    // Carica roster da Firestore
    const loadRosters = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const snapshot = await getDocs(collection(db, 'rosters'));
            const rostersData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setRosters(rostersData);
        } catch (err) {
            console.error('Errore caricamento roster:', err);
            setError('Errore nel caricamento dei roster');
        } finally {
            setLoading(false);
        }
    }, []);

    // Carica utenti
    const loadUsers = useCallback(async () => {
        try {
            const snapshot = await getDocs(collection(db, 'users'));
            const usersData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setUsers(usersData);
        } catch (err) {
            console.error('Errore caricamento utenti:', err);
        }
    }, []);

    useEffect(() => {
        loadRosters();
        loadUsers();
    }, [loadRosters, loadUsers]);

    const resetForm = useCallback(() => {
        setEditingId(null);
        setShowCreateForm(false);
        setForm({
            name: '',
            dataInizioPunteggio: '',
            maxWrestlers: 8,
            maxSwap: 8,
            maxCredits: 400,
            owner: 'FantaWrestling',
            gameType: 'Indipendente',
            imageUrl: '',
            description: '',
            minMaleWrestlers: 0,
            minFemaleWrestlers: 0,
            onlyMainRoster: false,
            onlyNXT: false,
            hasBackstage: false,
            backstageCount: 0,
            hasMainEventer: true,
            mainEventerDurationDays: MAIN_EVENTER_DURATION_DAYS,
            mainEventerCooldownDays: MAIN_EVENTER_COOLDOWN_DAYS,
            isGM: false,
        });
        setError('');
        setSuccess('');
    }, []);

    const handleEdit = useCallback((roster) => {
        setEditingId(roster.id);
        setShowCreateForm(true);

        // Gestione data: converti Timestamp in Date se necessario
        let dataInizio = roster.dataInizioPunteggio;
        if (dataInizio && typeof dataInizio.toDate === 'function') {
            dataInizio = dataInizio.toDate().toISOString().substring(0, 10);
        } else if (dataInizio instanceof Date) {
            dataInizio = dataInizio.toISOString().substring(0, 10);
        } else {
            dataInizio = '';
        }

        setForm({
            name: roster.name || '',
            dataInizioPunteggio: dataInizio,
            maxWrestlers: roster.maxWrestlers || 8,
            maxSwap: roster.maxSwap ?? 8,
            maxCredits: roster.maxCredits || (roster.maxWrestlers || 8) * 50,
            owner: roster.owner || 'FantaWrestling',
            gameType: roster.gameType || 'Indipendente',
            imageUrl: roster.imageUrl || '',
            description: roster.description || '',
            minMaleWrestlers: roster.minMaleWrestlers || 0,
            minFemaleWrestlers: roster.minFemaleWrestlers || 0,
            onlyMainRoster: roster.onlyMainRoster || false,
            onlyNXT: roster.onlyNXT || false,
            hasBackstage: roster.hasBackstage || false,
            backstageCount: roster.backstageCount || 0,
            hasMainEventer: roster.hasMainEventer !== false,
            mainEventerDurationDays: clampMainEventerTimingDays(
                roster.mainEventerDurationDays,
                MAIN_EVENTER_DURATION_DAYS
            ),
            mainEventerCooldownDays: clampMainEventerTimingDays(
                roster.mainEventerCooldownDays,
                MAIN_EVENTER_COOLDOWN_DAYS
            ),
            isGM: roster.isGM || false,
        });
        setError('');
        setSuccess('');
    }, []);

    const handleDelete = useCallback(async (id) => {
        if (!window.confirm('Sei sicuro di voler eliminare questo roster?')) return;

        setLoading(true);
        setError('');
        try {
            const roster = rosters.find(item => item.id === id);
            await deleteDoc(doc(db, 'rosters', id));
            await recordAdminUpdate({
                type: 'Roster',
                action: 'Eliminazione',
                title: roster?.name || id,
                description: `Roster eliminato: ${roster?.name || id}`,
                entityType: 'rosters',
                entityId: id
            });
            await loadRosters();
            setSuccess('Roster eliminato con successo');

            if (editingId === id) {
                resetForm();
            }
        } catch (err) {
            console.error('Errore eliminazione:', err);
            setError('Errore durante l\'eliminazione del roster');
        } finally {
            setLoading(false);
        }
    }, [editingId, loadRosters, resetForm, rosters]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Validazione base
        if (!form.name?.trim()) {
            setError('Il nome del roster è obbligatorio');
            return;
        }
        if (!form.dataInizioPunteggio) {
            setError('La data inizio punteggio è obbligatoria');
            return;
        }
        if (form.onlyMainRoster && form.onlyNXT) {
            setError('Non puoi attivare contemporaneamente Solo Main Roster e Solo NXT');
            return;
        }
        if (form.hasBackstage && (form.backstageCount < 1 || form.backstageCount > Math.max(form.maxWrestlers - (form.hasMainEventer ? 1 : 0), 0))) {
            setError(`Il numero di wrestler in backstage deve essere compreso tra 1 e ${Math.max(form.maxWrestlers - (form.hasMainEventer ? 1 : 0), 0)}`);
            return;
        }

        setLoading(true);
        try {
            const gameType = editingId ? currentEditingRoster?.gameType || form.gameType : form.gameType;
            const rosterData = {
                name: form.name.trim(),
                dataInizioPunteggio: Timestamp.fromDate(new Date(form.dataInizioPunteggio)),
                maxWrestlers: Number(form.maxWrestlers) || 8,
                maxSwap: Math.max(0, Number(form.maxSwap) || 0),
                maxCredits: Number(form.maxCredits) || (Number(form.maxWrestlers) || 8) * 50,
                owner: form.owner || 'FantaWrestling',
                imageUrl: form.imageUrl || '',
                description: form.description || `Roster indipendente creato automaticamente prima del Premium Live Event : ${form.name}`,
                minMaleWrestlers: Number(form.minMaleWrestlers) || 0,
                minFemaleWrestlers: Number(form.minFemaleWrestlers) || 0,
                onlyMainRoster: Boolean(form.onlyMainRoster),
                onlyNXT: Boolean(form.onlyNXT),
                hasBackstage: Boolean(form.hasBackstage),
                backstageCount: form.hasBackstage ? Number(form.backstageCount) || 0 : 0,
                hasMainEventer: Boolean(form.hasMainEventer),
                mainEventerDurationDays: clampMainEventerTimingDays(
                    form.mainEventerDurationDays,
                    MAIN_EVENTER_DURATION_DAYS
                ),
                mainEventerCooldownDays: clampMainEventerTimingDays(
                    form.mainEventerCooldownDays,
                    MAIN_EVENTER_COOLDOWN_DAYS
                ),
                isGM: Boolean(form.isGM),
            };

            if (editingId) {
                // Verifica se è un roster competitivo
                const currentRoster = rosters.find(r => r.id === editingId);
                if (currentRoster?.gameType === 'Competitivo') {
                    setError('Non è possibile modificare un roster competitivo');
                    setLoading(false);
                    return;
                }

                const nextRosterData = {
                    ...rosterData,
                    gameType
                };

                if (gameType === 'Indipendente') {
                    nextRosterData.isPublished = true;
                    nextRosterData.createdAt = currentRoster?.createdAt || serverTimestamp();
                    if (isRosterDraft(currentRoster) || !currentRoster?.publishedAt) {
                        nextRosterData.publishedAt = serverTimestamp();
                    }
                }

                await updateDoc(doc(db, 'rosters', editingId), nextRosterData);
                await recordAdminUpdate({
                    type: 'Roster',
                    action: 'Modifica',
                    title: rosterData.name,
                    description: buildChangeDescription('Roster modificato', currentRoster, nextRosterData, [
                        { key: 'name', label: 'nome' },
                        { key: 'imageUrl', label: 'immagine', renderChange: () => 'immagine aggiornata' },
                        { key: 'description', label: 'descrizione' },
                        { key: 'dataInizioPunteggio', label: 'data inizio punteggio', format: formatAdminDate },
                        { key: 'maxWrestlers', label: 'max wrestler' },
                        { key: 'maxSwap', label: 'max swap' },
                        { key: 'maxCredits', label: 'max crediti' },
                        { key: 'gameType', label: 'tipo gioco' },
                        { key: 'isPublished', label: 'stato', format: value => value === false ? 'bozza' : 'pubblicato' },
                        { key: 'minMaleWrestlers', label: 'min uomini' },
                        { key: 'minFemaleWrestlers', label: 'min donne' },
                        { key: 'onlyMainRoster', label: 'solo main roster', format: formatAdminBoolean },
                        { key: 'onlyNXT', label: 'solo NXT', format: formatAdminBoolean },
                        { key: 'hasBackstage', label: 'backstage', format: formatAdminBoolean },
                        { key: 'backstageCount', label: 'numero backstage' },
                        { key: 'hasMainEventer', label: 'main eventer', format: formatAdminBoolean },
                        { key: 'mainEventerDurationDays', label: 'durata main eventer (giorni)' },
                        { key: 'mainEventerCooldownDays', label: 'cooldown main eventer (giorni)' },
                        { key: 'isGM', label: 'GM', format: formatAdminBoolean }
                    ]),
                    entityType: 'rosters',
                    entityId: editingId
                });
                setSuccess(isRosterDraft(currentRoster) ? 'Roster creato con successo! Iscrizioni aperte.' : 'Roster aggiornato con successo!');
            } else {
                const rosterPayload = {
                    ...rosterData,
                    gameType
                };

                if (gameType === 'Indipendente') {
                    rosterPayload.isPublished = true;
                    rosterPayload.createdAt = serverTimestamp();
                    rosterPayload.publishedAt = serverTimestamp();
                } else {
                    rosterPayload.createdAt = serverTimestamp();
                }

                const rosterRef = await addDoc(collection(db, 'rosters'), rosterPayload);
                await recordAdminUpdate({
                    type: 'Roster',
                    action: 'Creazione',
                    title: rosterData.name,
                    description: `Nuovo roster creato: ${rosterData.name}`,
                    entityType: 'rosters',
                    entityId: rosterRef.id
                });
                setSuccess('Roster creato con successo!');
            }

            await loadRosters();
            resetForm();
        } catch (err) {
            console.error('Errore salvataggio:', err);
            setError('Errore durante il salvataggio del roster');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveDraft = useCallback(async () => {
        setError('');
        setSuccess('');

        const validationError = getRosterValidationError(form);
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        try {
            const gameType = editingId ? currentEditingRoster?.gameType || form.gameType : form.gameType;
            if (gameType !== 'Indipendente') {
                setError('Le bozze sono disponibili solo per i roster indipendenti pubblici');
                setLoading(false);
                return;
            }

            const rosterData = {
                name: form.name.trim(),
                dataInizioPunteggio: Timestamp.fromDate(new Date(form.dataInizioPunteggio)),
                maxWrestlers: Number(form.maxWrestlers) || 8,
                maxSwap: Math.max(0, Number(form.maxSwap) || 0),
                maxCredits: Number(form.maxCredits) || (Number(form.maxWrestlers) || 8) * 50,
                owner: form.owner || 'FantaWrestling',
                imageUrl: form.imageUrl || '',
                description: form.description || `Roster indipendente creato automaticamente prima del Premium Live Event : ${form.name}`,
                minMaleWrestlers: Number(form.minMaleWrestlers) || 0,
                minFemaleWrestlers: Number(form.minFemaleWrestlers) || 0,
                onlyMainRoster: Boolean(form.onlyMainRoster),
                onlyNXT: Boolean(form.onlyNXT),
                hasBackstage: Boolean(form.hasBackstage),
                backstageCount: form.hasBackstage ? Number(form.backstageCount) || 0 : 0,
                hasMainEventer: Boolean(form.hasMainEventer),
                mainEventerDurationDays: clampMainEventerTimingDays(
                    form.mainEventerDurationDays,
                    MAIN_EVENTER_DURATION_DAYS
                ),
                mainEventerCooldownDays: clampMainEventerTimingDays(
                    form.mainEventerCooldownDays,
                    MAIN_EVENTER_COOLDOWN_DAYS
                ),
                isGM: Boolean(form.isGM),
                gameType,
                isPublished: false,
                draftSavedAt: serverTimestamp()
            };

            if (editingId) {
                const currentRoster = rosters.find(r => r.id === editingId);
                if (currentRoster?.gameType === 'Competitivo') {
                    setError('Non è possibile modificare un roster competitivo');
                    setLoading(false);
                    return;
                }

                await updateDoc(doc(db, 'rosters', editingId), rosterData);
                await recordAdminUpdate({
                    type: 'Roster',
                    action: 'Salvataggio bozza',
                    title: rosterData.name,
                    description: `Bozza roster aggiornata: ${rosterData.name}`,
                    entityType: 'rosters',
                    entityId: editingId
                });
                setSuccess('Bozza aggiornata con successo!');
            } else {
                const rosterRef = await addDoc(collection(db, 'rosters'), rosterData);
                await recordAdminUpdate({
                    type: 'Roster',
                    action: 'Salvataggio bozza',
                    title: rosterData.name,
                    description: `Nuova bozza roster salvata: ${rosterData.name}`,
                    entityType: 'rosters',
                    entityId: rosterRef.id
                });
                setSuccess('Bozza salvata con successo!');
            }

            await loadRosters();
            resetForm();
        } catch (err) {
            console.error('Errore salvataggio bozza:', err);
            setError('Errore durante il salvataggio della bozza');
        } finally {
            setLoading(false);
        }
    }, [currentEditingRoster, editingId, form, loadRosters, resetForm, rosters]);

    const handlePublishDraft = useCallback(async (roster) => {
        if (!roster?.id || !isRosterDraft(roster)) return;

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            await updateDoc(doc(db, 'rosters', roster.id), {
                isPublished: true,
                createdAt: roster.createdAt || serverTimestamp(),
                publishedAt: roster.publishedAt || serverTimestamp()
            });
            await recordAdminUpdate({
                type: 'Roster',
                action: 'Pubblicazione',
                title: roster.name,
                description: `Iscrizioni aperte per il roster: ${roster.name}`,
                entityType: 'rosters',
                entityId: roster.id
            });
            await loadRosters();

            if (editingId === roster.id) {
                resetForm();
            }

            setSuccess(`Roster "${roster.name}" creato con successo!`);
        } catch (err) {
            console.error('Errore pubblicazione roster:', err);
            setError('Errore durante la pubblicazione del roster');
        } finally {
            setLoading(false);
        }
    }, [editingId, loadRosters, resetForm]);

    const handleInputChange = useCallback((field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    }, []);

    const filteredAndSortedRosters = useMemo(() => {
        let result = [...rosters];

        // Filtra per ricerca
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase().trim();
            result = result.filter(r =>
                r.name?.toLowerCase().includes(term) ||
                r.gameType?.toLowerCase().includes(term) ||
                getRosterStatus(r).label.toLowerCase().includes(term) ||
                r.description?.toLowerCase().includes(term) ||
                getRosterOwnerLabel(r, usersMap).toLowerCase().includes(term) ||
                (Array.isArray(r.participantInviteEmails) && r.participantInviteEmails.some(email => email?.toLowerCase().includes(term)))
            );
        }

        // Ordina
        result.sort((a, b) => {
            let aValue;
            let bValue;

            switch (sortField) {
                case 'maxWrestlers':
                case 'maxCredits':
                case 'maxSwap':
                case 'minMaleWrestlers':
                case 'minFemaleWrestlers':
                    aValue = Number(a[sortField]) || 0;
                    bValue = Number(b[sortField]) || 0;
                    break;
                case 'hasMainEventer':
                    aValue = a.hasMainEventer ? 1 : 0;
                    bValue = b.hasMainEventer ? 1 : 0;
                    break;
                case 'hasBackstage':
                    aValue = a.hasBackstage ? Number(a.backstageCount) || 0 : 0;
                    bValue = b.hasBackstage ? Number(b.backstageCount) || 0 : 0;
                    break;
                case 'isGM':
                    aValue = a.isGM ? 1 : 0;
                    bValue = b.isGM ? 1 : 0;
                    break;
                case 'filterRoster':
                    aValue = getRosterFilterLabel(a).toLowerCase();
                    bValue = getRosterFilterLabel(b).toLowerCase();
                    break;
                case 'participants':
                    aValue = Array.isArray(a.participantIds) ? a.participantIds.length : 0;
                    bValue = Array.isArray(b.participantIds) ? b.participantIds.length : 0;
                    break;
                case 'dataInizioPunteggio':
                    aValue = normalizeRosterDate(a.dataInizioPunteggio)?.getTime() || 0;
                    bValue = normalizeRosterDate(b.dataInizioPunteggio)?.getTime() || 0;
                    break;
                case 'createdAt':
                    aValue = normalizeRosterDate(a.createdAt)?.getTime() || 0;
                    bValue = normalizeRosterDate(b.createdAt)?.getTime() || 0;
                    break;
                case 'gameType':
                    aValue = (a.gameType || 'Indipendente').toString().toLowerCase();
                    bValue = (b.gameType || 'Indipendente').toString().toLowerCase();
                    break;
                case 'name':
                default:
                    aValue = (a.name || '').toString().toLowerCase();
                    bValue = (b.name || '').toString().toLowerCase();
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        return result;
    }, [rosters, searchTerm, sortField, sortDirection, usersMap]);

    const renderSortIndicator = useCallback((field) => (
        sortField === field
            ? <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill ms-1`}></i>
            : null
    ), [sortDirection, sortField]);

    const handleSort = useCallback((field) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    }, [sortField]);

    return (
        <div className="container admin-page py-4">
            <div className="row g-4">
                {/* Colonna form */}
                <div className="col-lg-12">
                    <div className="card shadow-sm">
                        <div className="card-header bg-primary text-white py-3 d-flex align-items-center justify-content-between">
                            <h5 className="mb-0">
                                <i className={`bi ${editingId ? 'bi-pencil-square' : 'bi-plus-circle'} me-2`}></i>
                                {editingId ? 'Modifica Roster' : 'Nuovo Roster'}
                            </h5>
                            {!editingId && (
                                <button
                                    type="button"
                                    className="btn btn-outline-light btn-sm"
                                    onClick={() => setShowCreateForm(prev => !prev)}
                                >
                                    <i className={`bi ${showCreateForm ? 'bi-dash-circle' : 'bi-plus-circle'} me-1`}></i>
                                    {showCreateForm ? 'Chiudi' : 'Apri'}
                                </button>
                            )}
                        </div>

                        {(editingId || showCreateForm) && (
                            <div className="card-body">
                                <form onSubmit={handleSubmit}>


                                    {/* Nome Roster */}
                                    <div className="mb-3">
                                        <div className="form-info-row mb-2">
                                            <label className="form-label fw-bold mb-0">
                                                Nome Roster <span className="text-danger">*</span>
                                            </label>
                                            <FormInfoTip title={rosterFormInfo.name.title}>
                                                {rosterFormInfo.name.body}
                                            </FormInfoTip>
                                        </div>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={form.name}
                                            onChange={e => handleInputChange('name', e.target.value)}
                                            placeholder="Es. SummerSlam 2024"
                                            required
                                            disabled={editingId && form.gameType === 'Competitivo'}
                                            maxLength={100}
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <div className="form-info-row mb-2">
                                            <label className="form-label fw-bold d-block mb-0">
                                                Immagine roster
                                            </label>
                                            <FormInfoTip title={rosterFormInfo.image.title}>
                                                {rosterFormInfo.image.body}
                                            </FormInfoTip>
                                        </div>
                                        <ImageUploader
                                            currentImageUrl={form.imageUrl}
                                            onUploadSuccess={(url) => handleInputChange('imageUrl', url)}
                                            storagePath="rosters"
                                            buttonText={form.imageUrl ? 'Cambia immagine' : 'Carica immagine'}
                                        />
                                        {form.imageUrl && (
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={() => handleInputChange('imageUrl', '')}
                                                disabled={editingId && form.gameType === 'Competitivo'}
                                            >
                                                <i className="bi bi-x-circle me-1"></i>
                                                Rimuovi immagine
                                            </button>
                                        )}
                                    </div>

                                    {/* Data inizio punteggio */}
                                    <div className="mb-3">
                                        <div className="form-info-row mb-2">
                                            <label className="form-label fw-bold mb-0">
                                                Data inizio punteggio <span className="text-danger">*</span>
                                            </label>
                                            <FormInfoTip title={rosterFormInfo.startDate.title}>
                                                {rosterFormInfo.startDate.body}
                                            </FormInfoTip>
                                        </div>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={form.dataInizioPunteggio}
                                            onChange={e => handleInputChange('dataInizioPunteggio', e.target.value)}
                                            required
                                            disabled={editingId && form.gameType === 'Competitivo'}
                                        />
                                    </div>

                                    {/* Max Wrestler - Range */}
                                    <div className="mb-4">
                                        <div className="form-info-row--between mb-2">
                                            <div className="form-info-row">
                                                <label className="form-label fw-bold mb-0">Max Wrestler</label>
                                                <FormInfoTip title="Max Wrestler">
                                                    {rosterFormInfo.slotsAndCredits.body} {` `}
                                                    Ogni fazione potra avere al massimo questo numero di wrestler.
                                                </FormInfoTip>
                                            </div>
                                            <span className="badge bg-primary rounded-pill">{form.maxWrestlers}</span>
                                        </div>
                                        <input
                                            type="range"
                                            className="form-range"
                                            min={6}
                                            max={20}
                                            step={1}
                                            value={form.maxWrestlers}
                                            onChange={e => handleInputChange('maxWrestlers', parseInt(e.target.value))}
                                            disabled={editingId && form.gameType === 'Competitivo'}
                                        />
                                        <div className="d-flex justify-content-between text-muted small">
                                            <span>Min: 6</span>
                                            <span>Max: 20</span>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <div className="form-info-row mb-2">
                                            <label className="form-label fw-bold mb-0">Crediti massimi</label>
                                            <FormInfoTip title="Crediti massimi">
                                                {rosterFormInfo.slotsAndCredits.body} {` `}
                                                E il budget iniziale a disposizione di ogni giocatore per costruire la propria fazione.
                                            </FormInfoTip>
                                        </div>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={form.maxCredits}
                                            onChange={e => handleInputChange('maxCredits', parseInt(e.target.value) || 0)}
                                            min={1}
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <div className="form-info-row--between mb-2">
                                            <div className="form-info-row">
                                                <label className="form-label fw-bold mb-0">Scambi massimi</label>
                                                <FormInfoTip title="Scambi massimi">
                                                    Numero massimo di operazioni di mercato consentite per ogni fazione all&apos;interno di questo roster.
                                                </FormInfoTip>
                                            </div>
                                            <span className="badge bg-primary rounded-pill">{form.maxSwap}</span>
                                        </div>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={form.maxSwap}
                                            onChange={e => handleInputChange('maxSwap', Math.max(0, parseInt(e.target.value, 10) || 0))}
                                            min={0}
                                            disabled={editingId && form.gameType === 'Competitivo'}
                                        />
                                        <small className="text-muted">Numero massimo di scambi consentiti per ogni fazione del roster.</small>
                                    </div>

                                    {/* Minimi uomini/donne - Range */}
                                    <div className="row">
                                        <div className="col-md-6 mb-4">
                                            <div className="form-info-row--between mb-2">
                                                <div className="form-info-row">
                                                    <label className="form-label fw-bold mb-0">Min. Uomini</label>
                                                    <FormInfoTip title={rosterFormInfo.composition.title}>
                                                        {rosterFormInfo.composition.body}
                                                    </FormInfoTip>
                                                </div>
                                                <span className="badge bg-info rounded-pill">{form.minMaleWrestlers}</span>
                                            </div>
                                            <input
                                                type="range"
                                                className="form-range"
                                                min={0}
                                                max={form.maxWrestlers / 2}
                                                step={1}
                                                value={form.minMaleWrestlers}
                                                onChange={e => handleInputChange('minMaleWrestlers', parseInt(e.target.value))}
                                                disabled={editingId && form.gameType === 'Competitivo'}
                                            />
                                            <div className="d-flex justify-content-between text-muted small">
                                                <span>0</span>
                                                <span>Max: {form.maxWrestlers / 2}</span>
                                            </div>
                                        </div>

                                        <div className="col-md-6 mb-4">
                                            <div className="form-info-row--between mb-2">
                                                <div className="form-info-row">
                                                    <label className="form-label fw-bold mb-0">Min. Donne</label>
                                                    <FormInfoTip title={rosterFormInfo.composition.title}>
                                                        {rosterFormInfo.composition.body}
                                                    </FormInfoTip>
                                                </div>
                                                <span className="badge bg-info rounded-pill">{form.minFemaleWrestlers}</span>
                                            </div>
                                            <input
                                                type="range"
                                                className="form-range"
                                                min={0}
                                                max={form.maxWrestlers / 2}
                                                step={1}
                                                value={form.minFemaleWrestlers}
                                                onChange={e => handleInputChange('minFemaleWrestlers', parseInt(e.target.value))}
                                                disabled={editingId && form.gameType === 'Competitivo'}
                                            />
                                            <div className="d-flex justify-content-between text-muted small">
                                                <span>0</span>
                                                <span>Max: {form.maxWrestlers / 2}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Validazione minima totale */}
                                    {form.minMaleWrestlers + form.minFemaleWrestlers > form.maxWrestlers && (
                                        <div className="alert alert-warning py-2 mb-3">
                                            <i className="bi bi-exclamation-triangle me-2"></i>
                                            La somma dei minimi ({form.minMaleWrestlers + form.minFemaleWrestlers}) supera il max wrestler ({form.maxWrestlers})
                                        </div>
                                    )}

                                    {/* Owner */}
                                    <div className="mb-4">
                                        <label className="form-label fw-bold">Owner</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={form.owner}
                                            onChange={e => handleInputChange('owner', e.target.value)}
                                            placeholder="FantaWrestling"
                                            disabled={editingId && form.gameType === 'Competitivo'}
                                        />
                                    </div>

                                    {/* Tipo di gioco - visibile solo in creazione */}
                                    {!editingId && (
                                        <div className="mb-4">
                                            <div className="form-info-row mb-2">
                                                <label className="form-label fw-bold mb-0">Tipo di gioco</label>
                                                <FormInfoTip title={rosterFormInfo.gameType.title}>
                                                    {rosterFormInfo.gameType.body}
                                                </FormInfoTip>
                                            </div>
                                            <div className="d-flex gap-3">
                                                <div className="form-check form-check-inline">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="gameType"
                                                        id="gameTypeIndipendente"
                                                        value="Indipendente"
                                                        checked={form.gameType === 'Indipendente'}
                                                        onChange={e => handleInputChange('gameType', e.target.value)}
                                                    />
                                                    <label className="form-check-label" htmlFor="gameTypeIndipendente">
                                                        Indipendente
                                                    </label>
                                                </div>
                                                <div className="form-check form-check-inline">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="gameType"
                                                        id="gameTypeCompetitivo"
                                                        value="Competitivo"
                                                        checked={form.gameType === 'Competitivo'}
                                                        onChange={e => handleInputChange('gameType', e.target.value)}
                                                    />
                                                    <label className="form-check-label" htmlFor="gameTypeCompetitivo">
                                                        Road To
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Filtri roster - Toggle Switch */}
                                    {/* Filtri roster - Toggle Switch con autoesclusione */}
                                    <div className="form-info-row mb-2">
                                        <span className="form-label fw-bold mb-0">Filtri e ruoli speciali</span>
                                        <FormInfoTip title={rosterFormInfo.options.title}>
                                            {rosterFormInfo.options.body}
                                        </FormInfoTip>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-md-6 mb-3">
                                            <div className="form-info-row mb-2">
                                                <label className="form-label fw-bold mb-0">Solo Main Roster</label>
                                                <FormInfoTip title={rosterFormInfo.onlyMainRoster.title}>
                                                    {rosterFormInfo.onlyMainRoster.body}
                                                </FormInfoTip>
                                            </div>
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    id="onlyMainRoster"
                                                    checked={form.onlyMainRoster}
                                                    onChange={e => {
                                                        // Se attivo soloMainRoster, disattiva soloNXT
                                                        handleInputChange('onlyMainRoster', e.target.checked);
                                                        if (e.target.checked && form.onlyNXT) {
                                                            handleInputChange('onlyNXT', false);
                                                        }
                                                    }}
                                                    disabled={editingId && form.gameType === 'Competitivo'}
                                                    style={{ cursor: 'pointer', height: '1.5em', width: '3em' }}
                                                />
                                                <label className="form-check-label ms-2" htmlFor="onlyMainRoster">
                                                    {form.onlyMainRoster ? 'Attivo' : 'Disattivo'}
                                                </label>
                                            </div>
                                            <small className="text-muted d-block mt-1">
                                                Seleziona solo wrestler del main roster
                                            </small>
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <div className="form-info-row mb-2">
                                                <label className="form-label fw-bold mb-0">Solo NXT</label>
                                                <FormInfoTip title={rosterFormInfo.onlyNXT.title}>
                                                    {rosterFormInfo.onlyNXT.body}
                                                </FormInfoTip>
                                            </div>
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    id="onlyNXT"
                                                    checked={form.onlyNXT}
                                                    onChange={e => {
                                                        // Se attivo soloNXT, disattiva onlyMainRoster
                                                        handleInputChange('onlyNXT', e.target.checked);
                                                        if (e.target.checked && form.onlyMainRoster) {
                                                            handleInputChange('onlyMainRoster', false);
                                                        }
                                                    }}
                                                    disabled={editingId && form.gameType === 'Competitivo'}
                                                    style={{ cursor: 'pointer', height: '1.5em', width: '3em' }}
                                                />
                                                <label className="form-check-label ms-2" htmlFor="onlyNXT">
                                                    {form.onlyNXT ? 'Attivo' : 'Disattivo'}
                                                </label>
                                            </div>
                                            <small className="text-muted d-block mt-1">
                                                Seleziona solo wrestler di NXT
                                            </small>
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <div className="col-md-4 mb-3">
                                            <div className="form-info-row mb-2">
                                                <label className="form-label fw-bold mb-0">Backstage</label>
                                                <FormInfoTip title={rosterFormInfo.backstage.title}>
                                                    {rosterFormInfo.backstage.body}
                                                </FormInfoTip>
                                            </div>
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    id="hasBackstage"
                                                    checked={form.hasBackstage}
                                                    onChange={e => handleInputChange('hasBackstage', e.target.checked)}
                                                />
                                                <label className="form-check-label ms-2" htmlFor="hasBackstage">
                                                    {form.hasBackstage ? 'Attivo' : 'Disattivo'}
                                                </label>
                                            </div>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <div className="form-info-row mb-2">
                                                <label className="form-label fw-bold mb-0">Main Eventer</label>
                                                <FormInfoTip title={rosterFormInfo.mainEventer.title}>
                                                    {rosterFormInfo.mainEventer.body}
                                                </FormInfoTip>
                                            </div>
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    id="hasMainEventer"
                                                    checked={form.hasMainEventer}
                                                    onChange={e => handleInputChange('hasMainEventer', e.target.checked)}
                                                />
                                                <label className="form-check-label ms-2" htmlFor="hasMainEventer">
                                                    {form.hasMainEventer ? 'Attivo' : 'Disattivo'}
                                                </label>
                                            </div>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <div className="form-info-row mb-2">
                                                <label className="form-label fw-bold mb-0">Modalita GM</label>
                                                <FormInfoTip title={rosterFormInfo.gmMode.title}>
                                                    {rosterFormInfo.gmMode.body}
                                                </FormInfoTip>
                                            </div>
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    id="isGM"
                                                    checked={form.isGM}
                                                    onChange={e => handleInputChange('isGM', e.target.checked)}
                                                />
                                                <label className="form-check-label ms-2" htmlFor="isGM">
                                                    {form.isGM ? 'Attivo' : 'Disattivo'}
                                                </label>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Messaggio informativo quando nessun filtro è selezionato */}
                                    {!form.onlyMainRoster && !form.onlyNXT && (
                                        <div className="alert alert-light py-2 mb-3 border">
                                            <i className="bi bi-info-circle me-2"></i>
                                            Nessun filtro attivo - verranno selezionati tutti i wrestler disponibili
                                        </div>
                                    )}

                                    <MainEventerTimingFields
                                        enabled={form.hasMainEventer}
                                        durationDays={form.mainEventerDurationDays}
                                        cooldownDays={form.mainEventerCooldownDays}
                                        onDurationChange={value => handleInputChange('mainEventerDurationDays', value)}
                                        onCooldownChange={value => handleInputChange('mainEventerCooldownDays', value)}
                                        idPrefix="admin-roster-main-eventer"
                                    />

                                    {/* Backstage Count - Range */}
                                    {form.hasBackstage && (
                                        <div className="mb-4">
                                            <div className="form-info-row--between mb-2">
                                                <div className="form-info-row">
                                                    <label className="form-label fw-bold mb-0">Numero wrestler in Backstage</label>
                                                    <FormInfoTip title={rosterFormInfo.backstageCount.title}>
                                                        {rosterFormInfo.backstageCount.body}
                                                    </FormInfoTip>
                                                </div>
                                                <span className="badge bg-primary rounded-pill">{form.backstageCount}</span>
                                            </div>
                                            <input
                                                type="range"
                                                className="form-range"
                                                min={1}
                                                max={Math.max(form.maxWrestlers - (form.hasMainEventer ? 1 : 0), 1)}
                                                step={1}
                                                value={form.backstageCount}
                                                onChange={e => handleInputChange('backstageCount', parseInt(e.target.value))}
                                                disabled={editingId && form.gameType === 'Competitivo'}
                                            />
                                            <div className="d-flex justify-content-between text-muted small">
                                                <span>Min: 1</span>
                                                <span>Max: {Math.max(form.maxWrestlers - (form.hasMainEventer ? 1 : 0), 1)}</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Messaggio quando un filtro è attivo */}
                                    {(form.onlyMainRoster || form.onlyNXT) && (
                                        <div className="alert alert-info py-2 mb-3">
                                            <i className="bi bi-funnel-fill me-2"></i>
                                            {form.onlyMainRoster && form.onlyNXT ? (
                                                <>Filtro attivo: wrestler presenti sia in main roster che in NXT</>
                                            ) : form.onlyMainRoster ? (
                                                <>Filtro attivo: solo wrestler del <strong>main roster</strong></>
                                            ) : (
                                                <>Filtro attivo: solo wrestler di <strong>NXT</strong></>
                                            )}
                                        </div>
                                    )}

                                    {/* Avviso se entrambi i filtri sono attivi */}
                                    {form.onlyMainRoster && form.onlyNXT && (
                                        <div className="alert alert-info py-2 mb-3">
                                            <i className="bi bi-info-circle me-2"></i>
                                            Con entrambi i filtri attivi, verranno selezionati solo wrestler presenti sia in main roster che in NXT
                                        </div>
                                    )}

                                    {form.gameType === 'Indipendente' && (
                                        <div className="alert alert-warning mb-3">
                                            <div className="d-flex flex-column flex-md-row gap-2 justify-content-between align-items-md-center">
                                                <div>
                                                    <i className="bi bi-calendar2-plus me-2"></i>
                                                    <strong>{isEditingDraft ? 'Bozza pronta:' : 'Programmazione roster pubblico:'}</strong>
                                                    <span className="ms-1">
                                                        {isEditingDraft
                                                            ? 'questo roster non è ancora visibile agli utenti. Premi "Crea Roster" quando vuoi aprire le iscrizioni.'
                                                            : 'puoi salvare tutte le impostazioni come bozza e premere "Crea Roster" in un secondo momento per iniziare le iscrizioni.'}
                                                    </span>
                                                </div>
                                                {isEditingDraft && (
                                                    <span className="badge bg-warning text-dark">
                                                        <i className="bi bi-clock-history me-1"></i>
                                                        Bozza
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Anteprima descrizione */}
                                    {(!editingId || form.gameType === 'Indipendente') && (
                                        <div className="alert alert-info mb-3">
                                            <i className="bi bi-info-circle me-2"></i>
                                            <strong>Descrizione automatica:</strong>
                                            <br />
                                            Roster indipendente creato in occasione del Premium Live Event : <span className="fw-bold">{form.name || '[nome roster]'}</span>
                                        </div>
                                    )}

                                    {/* Messaggi di errore/successo */}
                                    {error && <div className="alert alert-danger">{error}</div>}
                                    {success && <div className="alert alert-success">{success}</div>}

                                    {/* Pulsanti azione */}
                                    <div className="d-flex flex-column flex-md-row gap-2 justify-content-end mt-3">
                                        {(editingId || showCreateForm) && (
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary"
                                                onClick={resetForm}
                                                disabled={loading}
                                            >
                                                <i className="bi bi-x-lg me-1"></i>
                                                Annulla
                                            </button>
                                        )}
                                        {canSaveDraft && (
                                            <button
                                                type="button"
                                                className="btn btn-outline-warning"
                                                onClick={handleSaveDraft}
                                                disabled={loading}
                                            >
                                                <i className="bi bi-clock-history me-1"></i>
                                                {editingId ? 'Aggiorna Bozza' : 'Salva Bozza'}
                                            </button>
                                        )}
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={loading || isCompetitiveEditLocked}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Salvataggio...
                                                </>
                                            ) : (
                                                editingId
                                                    ? (isEditingDraft ? 'Crea Roster' : 'Aggiorna Roster')
                                                    : 'Crea Roster'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>
                </div>

                {/* Colonna lista roster */}
                <div className="col-lg-12">
                    <div className="card shadow-sm">
                        <div className="card-header bg-light py-3">
                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                                <h5 className="mb-0">
                                    <i className="bi bi-list-ul me-2"></i>
                                    Roster ({filteredAndSortedRosters.length})
                                </h5>
                                <div className="input-group" style={{ maxWidth: '300px' }}>
                                    <span className="input-group-text bg-white">
                                        <i className="bi bi-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Cerca roster..."
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-dark table-striped mb-0 align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', minWidth: '150px' }}>
                                            <div className="d-flex align-items-center">
                                                <i className="bi bi-people-fill me-1"></i> Nome
                                                {renderSortIndicator('name')}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort('maxWrestlers')} style={{ cursor: 'pointer', minWidth: '80px' }}>
                                            <div className="d-flex align-items-center">
                                                Wrestler
                                                {renderSortIndicator('maxWrestlers')}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort('maxCredits')} style={{ cursor: 'pointer', minWidth: '80px' }}>
                                            <div className="d-flex align-items-center">
                                                Crediti
                                                {renderSortIndicator('maxCredits')}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort('maxSwap')} style={{ cursor: 'pointer', minWidth: '80px' }}>
                                            <div className="d-flex align-items-center">
                                                Scambi
                                                {renderSortIndicator('maxSwap')}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort('gameType')} style={{ cursor: 'pointer', minWidth: '110px' }}>
                                            <div className="d-flex align-items-center">
                                                Tipo Gioco
                                                {renderSortIndicator('gameType')}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort('hasMainEventer')} style={{ cursor: 'pointer', minWidth: '100px' }}>
                                            <div className="d-flex align-items-center">
                                                Main Eventer
                                                {renderSortIndicator('hasMainEventer')}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort('hasBackstage')} style={{ cursor: 'pointer', minWidth: '100px' }}>
                                            <div className="d-flex align-items-center">
                                                Backstage
                                                {renderSortIndicator('hasBackstage')}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort('isGM')} style={{ cursor: 'pointer', minWidth: '80px' }}>
                                            <div className="d-flex align-items-center">
                                                GM
                                                {renderSortIndicator('isGM')}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort('filterRoster')} style={{ cursor: 'pointer', minWidth: '110px' }}>
                                            <div className="d-flex align-items-center">
                                                Filtro Roster
                                                {renderSortIndicator('filterRoster')}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort('minMaleWrestlers')} style={{ cursor: 'pointer', minWidth: '80px' }}>
                                            <div className="d-flex align-items-center">
                                                Min Uomini
                                                {renderSortIndicator('minMaleWrestlers')}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort('minFemaleWrestlers')} style={{ cursor: 'pointer', minWidth: '80px' }}>
                                            <div className="d-flex align-items-center">
                                                Min Donne
                                                {renderSortIndicator('minFemaleWrestlers')}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort('participants')} style={{ cursor: 'pointer', minWidth: '100px' }}>
                                            <div className="d-flex align-items-center">
                                                Partecipanti
                                                {renderSortIndicator('participants')}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort('dataInizioPunteggio')} style={{ cursor: 'pointer', minWidth: '130px' }}>
                                            <div className="d-flex align-items-center">
                                                Data Inizio
                                                {renderSortIndicator('dataInizioPunteggio')}
                                            </div>
                                        </th>
                                        <th onClick={() => handleSort('createdAt')} style={{ cursor: 'pointer', minWidth: '130px' }}>
                                            <div className="d-flex align-items-center">
                                                Data Creazione
                                                {renderSortIndicator('createdAt')}
                                            </div>
                                        </th>
                                        <th style={{ width: '140px', textAlign: 'center' }}>Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAndSortedRosters.length > 0 ? (
                                        filteredAndSortedRosters.map(roster => {
                                            const startDate = normalizeRosterDate(roster.dataInizioPunteggio);
                                            const createdAt = normalizeRosterDate(roster.createdAt || roster.draftSavedAt);
                                            const participantsCount = Array.isArray(roster.participantIds) ? roster.participantIds.length : 0;
                                            const ownerLabel = getRosterOwnerLabel(roster, usersMap);
                                            const rosterStatus = getRosterStatus(roster);

                                            return (
                                                <tr key={roster.id}>
                                                    {/* Nome */}
                                                    <td className='text-light'>
                                                        <strong className="d-block">{roster.name}</strong>
                                                        <small className="text-muted">
                                                            <i className="bi bi-person-circle me-1"></i> {ownerLabel}
                                                        </small>
                                                        {roster.description && (
                                                            <small className="d-block text-muted mt-1 text-truncate" style={{ maxWidth: '200px' }} title={roster.description}>
                                                                <i className="bi bi-chat-text me-1"></i> {roster.description}
                                                            </small>
                                                        )}
                                                        <div className="d-flex flex-wrap gap-1 mt-2">
                                                            <span className={`badge ${roster.gameType === 'Competitivo' ? 'bg-brand-secondary text-brand-primary' : 'bg-brand-primary'}`}>
                                                                {roster.gameType || 'Indipendente'}
                                                            </span>
                                                            <span className={`badge ${rosterStatus.className}`}>
                                                                <i className={`bi ${rosterStatus.icon} me-1`}></i>
                                                                {rosterStatus.label}
                                                            </span>
                                                        </div>
                                                    </td>


                                                    {/* Max Wrestler */}
                                                    <td className="text-center fw-semibold">{roster.maxWrestlers || 0}</td>

                                                    {/* Crediti */}
                                                    <td className="text-center">{roster.maxCredits ?? '-'}</td>

                                                    {/* Scambi */}
                                                    <td className="text-center">{roster.maxSwap ?? 0}</td>

                                                    {/* Tipo Gioco */}
                                                    <td>
                                                        <span className={`badge ${roster.gameType === 'Competitivo' ? 'bg-brand-secondary text-brand-primary' : 'bg-brand-primary'}`}>
                                                            {roster.gameType || 'Indipendente'}
                                                        </span>
                                                    </td>

                                                    {/* Main Eventer */}
                                                    <td>
                                                        {roster.hasMainEventer ? (
                                                            <span className="badge bg-brand-accent bg-opacity-10 text-brand-secondary">
                                                                Sì
                                                            </span>
                                                        ) : (
                                                            <span className="badge bg-secondary bg-opacity-10 text-muted">No</span>
                                                        )}
                                                    </td>

                                                    {/* Backstage (con conteggio) */}
                                                    <td>
                                                        {roster.hasBackstage ? (
                                                            <span className="badge bg-info bg-opacity-10 text-info">
                                                                {roster.backstageCount || 0}
                                                            </span>
                                                        ) : (
                                                            <span className="badge bg-secondary bg-opacity-10 text-muted">No</span>
                                                        )}
                                                    </td>

                                                    {/* GM */}
                                                    <td>
                                                        {roster.isGM ? (
                                                            <span className="badge bg-purple text-warning">
                                                                Sì
                                                            </span>
                                                        ) : (
                                                            <span className="badge bg-secondary bg-opacity-10 text-muted">No</span>
                                                        )}
                                                    </td>

                                                    {/* Filtro Roster */}
                                                    <td>
                                                        {roster.onlyMainRoster && (
                                                            <span className="badge bg-brand-primary-ultralight bg-opacity-10 text-primary me-1">
                                                                Main
                                                            </span>
                                                        )}
                                                        {roster.onlyNXT && (
                                                            <span className="badge bg-brand-secondary bg-opacity-10 text-brand-primary">
                                                                NXT
                                                            </span>
                                                        )}
                                                        {!roster.onlyMainRoster && !roster.onlyNXT && (
                                                            <span className="badge bg-secondary bg-opacity-10 text-muted">Nessuno</span>
                                                        )}
                                                    </td>

                                                    {/* Min Uomini */}
                                                    <td className="text-center ">{roster.minMaleWrestlers || 0}</td>

                                                    {/* Min Donne */}
                                                    <td className="text-center">{roster.minFemaleWrestlers || 0}</td>

                                                    {/* Partecipanti (con posti liberi) */}
                                                    <td>
                                                        <div className="fw-semibold text-center">{participantsCount}</div>
                                                    </td>

                                                    {/* Data Inizio */}
                                                    <td className="small text-muted-brand">
                                                        {startDate ? startDate.toLocaleString('it-IT') : '-'}
                                                    </td>

                                                    {/* Data Creazione */}
                                                    <td className="small text-muted-brand">
                                                        {createdAt ? createdAt.toLocaleString('it-IT') : '-'}
                                                    </td>

                                                    {/* Azioni */}
                                                    <td className="text-center">
                                                        <div className="d-flex gap-1 justify-content-center">
                                                            {isRosterDraft(roster) && (
                                                                <button
                                                                    className="btn btn-sm btn-outline-success"
                                                                    onClick={() => handlePublishDraft(roster)}
                                                                    title="Crea roster e apri iscrizioni"
                                                                    disabled={loading}
                                                                >
                                                                    <i className="bi bi-play-circle"></i>
                                                                </button>
                                                            )}
                                                            <button
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => handleEdit(roster)}
                                                                title="Modifica roster"
                                                                disabled={loading}
                                                            >
                                                                <i className="bi bi-pencil"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDelete(roster.id)}
                                                                title="Elimina roster"
                                                                disabled={loading}
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="15" className="text-center py-5">
                                                <div className="text-muted">
                                                    <i className="bi bi-emoji-frown fs-1"></i>
                                                    <p className="mt-3 mb-0">Nessun roster trovato</p>
                                                    {searchTerm && (
                                                        <button className="btn btn-link" onClick={() => setSearchTerm('')}>
                                                            Cancella ricerca
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
